import React, { useState, useEffect, useCallback } from 'react';
import { UploadCloud, FileSpreadsheet, CheckCircle2, Clock, FileText, Trash2, TrendingUp, X } from 'lucide-react';
import FileUpload from './FileUpload';
import axios from 'axios';
import { formatDateTimeAU } from '../../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DataUploadView = ({ onProcess }) => {
  const [revenueFile, setRevenueFile] = useState(null);
  const [complianceFile, setComplianceFile] = useState(null);
  const [workloadFile, setWorkloadFile] = useState(null);
  const [npsFile, setNpsFile] = useState(null);
  const [meetingsFile, setMeetingsFile] = useState(null);
  const [callsFile, setCallsFile] = useState(null);
  const [everperformFile, setEverperformFile] = useState(null);
  const [masterlistFile, setMasterlistFile] = useState(null);
  const [masterlistMerge, setMasterlistMerge] = useState(true);
  // Revenue Tracker workbooks — TWO files uploaded as one tile.
  const [revenueTrackerFy26File, setRevenueTrackerFy26File] = useState(null);
  const [revenueTrackerFy27File, setRevenueTrackerFy27File] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [result, setResult] = useState(null);

  const fetchUploadHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/upload-history`);
      setUploadHistory(res.data || []);
    } catch (err) {
      // History endpoint is optional — log for visibility but don't break the UI.
      console.warn('Upload history unavailable:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchUploadHistory();
  }, [fetchUploadHistory]);

  const handleProcess = async () => {
    setIsProcessing(true);
    setResult(null);
    const files = {};

    if (masterlistFile) files.masterlist = { file: masterlistFile, merge: masterlistMerge };
    if (revenueFile) files.revenue = revenueFile;
    if (complianceFile) files.compliance = complianceFile;
    if (workloadFile) files.workload = workloadFile;
    if (npsFile) files.nps = npsFile;
    if (meetingsFile) files.meetings = meetingsFile;
    if (callsFile) files.calls = callsFile;
    if (everperformFile) files.everperform = everperformFile;
    if (revenueTrackerFy26File || revenueTrackerFy27File) {
      files.revenueTracker = {
        fy26: revenueTrackerFy26File,
        fy27: revenueTrackerFy27File,
      };
    }

    try {
      await onProcess(files);
      setResult({ success: true, count: Object.keys(files).length });
      // Clear files after successful upload
      setRevenueFile(null);
      setComplianceFile(null);
      setWorkloadFile(null);
      setNpsFile(null);
      setMeetingsFile(null);
      setCallsFile(null);
      setEverperformFile(null);
      setMasterlistFile(null);
      setRevenueTrackerFy26File(null);
      setRevenueTrackerFy27File(null);
      fetchUploadHistory();
    } catch (error) {
      setResult({ success: false, error: error.response?.data?.detail || error.message });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClearAll = () => {
    setRevenueFile(null);
    setComplianceFile(null);
    setWorkloadFile(null);
    setNpsFile(null);
    setMeetingsFile(null);
    setCallsFile(null);
    setEverperformFile(null);
    setMasterlistFile(null);
    setRevenueTrackerFy26File(null);
    setRevenueTrackerFy27File(null);
    setResult(null);
  };

  const filesSelected = revenueFile || complianceFile || workloadFile || npsFile || meetingsFile || callsFile || everperformFile || masterlistFile || revenueTrackerFy26File || revenueTrackerFy27File;
  const selectedCount = [revenueFile, complianceFile, workloadFile, npsFile, meetingsFile, callsFile, everperformFile, masterlistFile, revenueTrackerFy26File, revenueTrackerFy27File].filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="data-upload-view">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[#171717] font-heading">Data Upload</h1>
        <p className="text-sm text-[#737373] mt-1">Import your data files to update the analytics dashboard.</p>
      </div>

      {/* Success / Error Banner */}
      {result && (
        <div data-testid="upload-result-banner" className={`flex items-center gap-3 px-4 py-3 rounded-[10px] border ${
          result.success
            ? 'bg-[#ECFDF5] border-emerald-200 text-emerald-700'
            : 'bg-[#FEF2F2] border-red-200 text-red-700'
        }`}>
          {result.success ? (
            <>
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              <span className="text-sm font-medium">{result.count} file(s) uploaded and processed successfully. Dashboard data has been refreshed.</span>
            </>
          ) : (
            <>
              <span className="text-sm font-medium">Upload failed: {result.error}</span>
            </>
          )}
        </div>
      )}

      {/* Upload Grid */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-[8px] bg-[#F8F9FA] flex items-center justify-center">
              <FileSpreadsheet className="w-4 h-4 text-[#525252]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#171717] font-heading">Select Files</h2>
              <p className="text-xs text-[#737373]">Drag and drop or click to browse. Supports CSV and Excel formats.</p>
            </div>
          </div>
          {filesSelected && (
            <button
              data-testid="clear-all-files-btn"
              onClick={handleClearAll}
              className="flex items-center gap-1.5 text-xs font-medium text-[#737373] hover:text-red-500 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
              Clear All
            </button>
          )}
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <FileUpload
              label="Master List"
              description="Plan-Manager assignments (.xlsx)"
              file={masterlistFile}
              onFileSelect={setMasterlistFile}
              accept=".xlsx,.xls"
            />
            {masterlistFile && (
              <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-[8px] bg-[#F8F9FA] border border-[#E5E5E5]">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    data-testid="masterlist-merge-toggle"
                    type="checkbox"
                    checked={masterlistMerge}
                    onChange={(e) => setMasterlistMerge(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-8 h-4 bg-[#E5E5E5] peer-focus:ring-2 peer-focus:ring-[#171717]/10 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#171717]"></div>
                </label>
                <span className="text-[11px] text-[#525252]">
                  {masterlistMerge
                    ? 'Merge — preserves excluded plans'
                    : 'Full replace — overwrites all data'}
                </span>
              </div>
            )}
          </div>
          <FileUpload
            label="Revenue Data"
            description="Plan revenue & management fees"
            file={revenueFile}
            onFileSelect={setRevenueFile}
            accept=".csv,.xlsx,.xls"
          />
          <FileUpload
            label="Compliance Data"
            description="Plan compliance status"
            file={complianceFile}
            onFileSelect={setComplianceFile}
            accept=".csv,.xlsx,.xls"
          />
          <FileUpload
            label="Workload Data"
            description="Emails & handle times"
            file={workloadFile}
            onFileSelect={setWorkloadFile}
            accept=".csv,.xlsx,.xls"
          />
          <FileUpload
            label="NPS Data"
            description="Client satisfaction scores"
            file={npsFile}
            onFileSelect={setNpsFile}
            accept=".csv,.xlsx,.xls"
          />
          <FileUpload
            label="Meeting Data"
            description="COM, AGM & Special meetings"
            file={meetingsFile}
            onFileSelect={setMeetingsFile}
            accept=".csv,.xlsx,.xls"
          />
          <FileUpload
            label="Phone Call Data"
            description="Extension Stats — Total Calls export"
            file={callsFile}
            onFileSelect={setCallsFile}
            accept=".csv"
          />
          <FileUpload
            label="Everperform Pulse"
            description="Weekly pulse — Wellbeing, Compliance, Performance"
            file={everperformFile}
            onFileSelect={setEverperformFile}
            accept=".csv"
          />

          {/* Revenue Tracker Workbooks — dual slot, one card */}
          <div
            data-testid="file-upload-revenue-tracker-workbooks"
            className="md:col-span-2 border border-[#E5E5E5] rounded-[10px] p-4 bg-white"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-[8px] bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-emerald-700" strokeWidth={1.75} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#171717]">Revenue Tracker Workbooks</p>
                <p className="text-[11px] text-[#737373]">FY26 Actuals + FY27 Forecast — used for Revenue Tracker, Run-Rate vs Budget, YoY comparison and Revenue Detail.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <RevenueSlot
                fyLabel="FY26 Actuals"
                subtitle="Revenue Report v2 — 2025-26"
                file={revenueTrackerFy26File}
                onSelect={setRevenueTrackerFy26File}
                testId="rev-tracker-fy26-file"
              />
              <RevenueSlot
                fyLabel="FY27 Forecast"
                subtitle="Revenue Report v2 — 2026-27"
                file={revenueTrackerFy27File}
                onSelect={setRevenueTrackerFy27File}
                testId="rev-tracker-fy27-file"
              />
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="px-6 py-4 border-t border-[#E5E5E5] flex justify-between items-center bg-[#F8F9FA] rounded-b-[10px]">
          <div className="flex items-center text-sm text-[#737373]">
            {filesSelected ? (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500" strokeWidth={1.5} />
                <span className="text-[#525252] font-medium">{selectedCount} file(s) ready for processing</span>
              </>
            ) : (
              <span>Select at least one file to continue</span>
            )}
          </div>
          <button
            data-testid="process-files-btn"
            onClick={handleProcess}
            disabled={isProcessing || !filesSelected}
            className="bg-[#171717] text-white rounded-[8px] px-5 py-2.5 text-sm font-medium hover:bg-[#262626] transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#171717]"
          >
            {isProcessing ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <UploadCloud className="w-4 h-4" strokeWidth={1.5} />
                Process Files
              </>
            )}
          </button>
        </div>
      </div>

      {/* Upload History */}
      {uploadHistory.length > 0 && (
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center gap-3">
            <div className="w-9 h-9 rounded-[8px] bg-[#F8F9FA] flex items-center justify-center">
              <Clock className="w-4 h-4 text-[#525252]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#171717] font-heading">Recent Uploads</h2>
              <p className="text-xs text-[#737373]">History of your data imports</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8F9FA] border-y border-[#E5E5E5]">
                  <th className="text-left py-3 px-6 text-xs font-medium text-[#737373] uppercase tracking-wider">Data Type</th>
                  <th className="text-left py-3 px-6 text-xs font-medium text-[#737373] uppercase tracking-wider">File Name</th>
                  <th className="text-left py-3 px-6 text-xs font-medium text-[#737373] uppercase tracking-wider">Records</th>
                  <th className="text-left py-3 px-6 text-xs font-medium text-[#737373] uppercase tracking-wider">Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {uploadHistory.slice(0, 10).map((entry) => (
                  <tr key={entry.id} className="border-b border-[#E5E5E5] hover:bg-[#F8F9FA]/50 transition-colors">
                    <td className="py-3 px-6">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F8F9FA] text-[#525252] border border-[#E5E5E5]">
                        <FileText className="w-3 h-3" strokeWidth={1.5} />
                        {entry.dataType}
                      </span>
                    </td>
                    <td className="py-3 px-6 text-sm text-[#171717]">{entry.filename}</td>
                    <td className="py-3 px-6 text-sm text-[#525252]">{entry.recordCount?.toLocaleString()}</td>
                    <td className="py-3 px-6 text-sm text-[#737373]">
                      {entry.uploadedAt ? formatDateTimeAU(entry.uploadedAt) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataUploadView;

// Compact inline file slot for the dual-workbook tile.  Behaviour mirrors
// FileUpload but tighter chrome so both slots fit in one card.
const RevenueSlot = ({ fyLabel, subtitle, file, onSelect, testId }) => {
  const inputId = `rev-slot-${fyLabel}`.replace(/\s+/g, '-');
  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) onSelect(e.dataTransfer.files[0]);
  };
  return (
    <div
      data-testid={testId}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      onClick={() => document.getElementById(inputId)?.click()}
      className={`relative border-2 border-dashed rounded-[8px] p-3 flex items-center gap-3 cursor-pointer transition-colors
        ${file ? 'border-emerald-400 bg-[#ECFDF5]' : 'border-[#E5E5E5] hover:bg-[#F8F9FA] hover:border-[#A3A3A3]'}`}
    >
      <div className={`w-8 h-8 rounded-[6px] flex items-center justify-center flex-shrink-0 ${file ? 'bg-emerald-100' : 'bg-[#F8F9FA]'}`}>
        {file
          ? <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={1.75} />
          : <UploadCloud className="w-4 h-4 text-[#737373]" strokeWidth={1.5} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-[#171717]">{fyLabel}</p>
        <p className="text-[10.5px] text-[#737373] truncate">
          {file ? file.name : subtitle}
        </p>
      </div>
      {file && (
        <button
          type="button"
          data-testid={`${testId}-remove`}
          onClick={(e) => { e.stopPropagation(); onSelect(null); }}
          className="w-6 h-6 rounded-full hover:bg-white/60 flex items-center justify-center"
          aria-label="Remove file"
        >
          <X className="w-3.5 h-3.5 text-[#737373]" strokeWidth={2} />
        </button>
      )}
      <input
        id={inputId}
        type="file"
        accept=".xlsx,.xlsm,.xls"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && onSelect(e.target.files[0])}
      />
    </div>
  );
};
